import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from '@reach/router';
import Label from './Label';
import { RFC } from 'state/StateTypes';
import { Check, X } from 'styled-icons/boxicons-regular';
import { Asset } from 'state/assets/AssetTypes';
import { useDispatch } from 'react-redux';
import { updateAsset } from 'state/assets/AssetActions';
import { theme } from 'styles/ThemeProvider';
import { deriveDateFromMongoId, presentErrorBasedOnCode } from 'utils';
import LinkGroup from 'components/LinkGroup';
import { Error, ErrorCodes } from 'types';
import ErrorMessage from './ErrorMessage';
import SmallLoader from './SmallLoader';

interface Props {
    presentDeleteAssetConfirmationModal: (assetId: string) => void;
    asset: Asset;
}

const ListItemAsset: RFC<Props> = ({ asset, presentDeleteAssetConfirmationModal }) => {
    const dispatch = useDispatch();
    const [togglingStatus, setTogglingStatus] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const toggleStatus = async () => {
        try {
            setTogglingStatus(true);
            await dispatch(updateAsset({ active: !asset.active, _id: asset._id }));
        } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
        }
        setTogglingStatus(false);
    };

    return (
        <>
            <Container>
                <ItemInfo>
                    <span>
                        <Label>Name</Label>
                        {asset.name}
                    </span>
                    <span>
                        <Label>Created</Label> {deriveDateFromMongoId(asset._id)}
                    </span>
                    <span>
                        <Label>Tier</Label>
                        {asset.tier}
                    </span>
                    <span>
                        <Label>Active</Label>
                        {asset.active ? (
                            <Check
                                size={30}
                                title="User is active"
                                fontWeight={300}
                                color={theme.secondaryGreen}
                                style={{ marginBottom: '5px' }}
                            />
                        ) : (
                            <X
                                size={30}
                                title="User is not active"
                                fontWeight={300}
                                color={theme.secondaryRed}
                                style={{ marginBottom: '5px' }}
                            />
                        )}
                    </span>
                </ItemInfo>
                <LinkGroup>
                    <li>
                        <Link to={`${window.location.pathname}/${asset._id}`}>Details</Link>
                    </li>
                    <li>
                        <Link to={`${window.location.pathname}/${asset._id}/edit`}>Edit</Link>
                    </li>
                    <li>
                        <ToggleStatus onClick={toggleStatus}>
                            {asset.active ? (
                                togglingStatus ? (
                                    <SmallLoader />
                                ) : (
                                    'Deactivate'
                                )
                            ) : togglingStatus ? (
                                <SmallLoader />
                            ) : (
                                'Activate'
                            )}
                        </ToggleStatus>
                    </li>
                    {/*
                        TODO: The comment around the `DeleteButton` is to remove it from the rendered ListItemAsset on the
                        Waveforms and Senesations pages for each asset.  Remove the comment below to show the delete button.
                     */}
                    {/* <li>
                  <DeleteButton onClick={() => presentDeleteAssetConfirmationModal(asset._id)}>Delete</DeleteButton>
               </li> */}
                </LinkGroup>
            </Container>
            {error && (
                <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>
            )}
        </>
    );
};

export default ListItemAsset;

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    flex-direction: row;
    border: 1px solid red;
    padding: 10px 20px;
    margin: 0 auto;
    background-color: ${({ theme }) => theme.secondaryWhite};
    align-items: center;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.primaryGrey};

    :not(:last-child) {
        margin-bottom: 10px;
    }
`;

const ItemInfo = styled.div`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 5px;
    @media only screen and (min-width: 1060px) {
        flex-direction: row;
        align-items: center;
        > * {
            margin: 0 15px;
            padding: 5px;
        }
    }
`;

const ToggleStatus = styled.a`
    cursor: pointer;
`;

const DeleteButton = styled.button.attrs({ type: 'button' })`
    background-color: transparent;
    color: ${({ theme }) => theme.primaryBlueDark};
    font-weight: 600;
    border: none;
    font-size: 16px;
    padding: 0;
    cursor: pointer;
`;
